<script setup lang="ts">
import Cookies from 'js-cookie';
import { onMounted, ref } from 'vue';

import { UserService } from '../../api/user/userService';
import { router } from '../../Router';
import { RoutePaths } from '../../Router/types/routePaths';
import { UserRole } from '@common/contracts';
import { useUserStore } from '../../stores/userStore/userStore';
import { userTokensCookieName, useUserTokensStore } from '../../stores/userTokensStore/userTokensStore';
import LoginForm from '../../components/Auth/LoginForm.vue';
import RegisterForm from '../../components/Auth/RegisterForm.vue';
import SuccessPopup from '../../components/UI/SuccessPopup/SuccessPopup.vue';

const isRegistering = ref(false);
const queryParams = router.currentRoute.value.query;
const loginError = ref<string | null>(null);
const showSuccessPopup = ref(false);

const userTokensStore = useUserTokensStore();
const userStore = useUserStore();

onMounted(async () => {
  if (queryParams && queryParams['code'] && queryParams['userId']) {
    const { code, userId } = queryParams;
    if (typeof code !== 'string' || typeof userId !== 'string') {
      return;
    }
    const response = await UserService.login({
      accessCode: code,
      userId,
    });
    userTokensStore.setUserTokens(response);
    userStore.setUser({
      login: '',
      id: userId,
      role: UserRole.guest,
      apps: [],
      marketingConsent: false,
    });
    router.push({
      path: RoutePaths.pictures,
      replace: true,
    });
  }
});

const toggleForm = () => {
  isRegistering.value = !isRegistering.value;
  loginError.value = null;
};

const handleRegisterSuccess = () => {
  isRegistering.value = false;
  showSuccessPopup.value = true;
  setTimeout(() => {
    showSuccessPopup.value = false;
  }, 5000);
};

userTokensStore.$subscribe(
  (_, state) => {
    if (state.userTokens) {
      Cookies.set(userTokensCookieName, JSON.stringify(state.userTokens));
    } else {
      Cookies.remove(userTokensCookieName);
    }
  },
  {
    detached: true,
  },
);
</script>

<template>
  <div class="auth-container">
    <div class="auth-content">
      <div class="auth-box">
        <div class="auth-header">
          <img
            src="../../assets/logo.png"
            alt="Logo"
            class="auth-logo"
          />
          <h1 class="auth-title">{{ !isRegistering ? 'Logowanie' : 'Rejestracja' }}</h1>
        </div>

        <LoginForm
          v-if="!isRegistering"
          @toggle-form="toggleForm"
        />
        <RegisterForm
          v-else
          @toggle-form="toggleForm"
          @success="handleRegisterSuccess"
        />
      </div>

      <div class="features-tile">
        <h2 class="features-tile__title">W naszej aplikacji możesz:</h2>
        <ul class="features-tile__list">
          <li class="features-tile__item">
            <span class="features-tile__check">✓</span>
            Zarządzać listą gości i potwierdzeniami RSVP
          </li>
          <li class="features-tile__item">
            <span class="features-tile__check">✓</span>
            Zbierać i organizować zdjęcia z wesela
          </li>
          <li class="features-tile__item">
            <span class="features-tile__check">✓</span>
            Udostępniać wspomnienia wszystkim gościom
          </li>
          <li class="features-tile__item">
            <span class="features-tile__check">✓</span>
            Tworzyć piękne albumy i kolekcje wspomnień
          </li>
        </ul>
      </div>
    </div>

    <SuccessPopup
      :visible="showSuccessPopup"
      title="Sukces!"
      message="Rejestracja zakończona pomyślnie. Aby się zalogować, potwierdź swój adres email klikając w link wysłany na Twoją skrzynkę."
      info-message="Sprawdź swoją skrzynkę email"
    />
  </div>
</template>

<style scoped lang="scss">
.auth-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, $white-100 0%, $brown-400 100%);
  padding: 20px;
}

.auth-content {
  display: flex;
  gap: 4rem;
  align-items: center;
  justify-content: center;
  max-width: 1000px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
    align-items: center;
  }
}

.auth-box {
  background: $white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba($black, 0.1);
  width: 100%;
  max-width: 480px;
  padding: 48px;

  @media (max-width: 768px) {
    padding: 32px;
  }

  @media (max-width: 480px) {
    padding: 24px;
  }
}

.auth-header {
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
}

.auth-logo {
  width: 200px;
  height: auto;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    width: 160px;
    margin-bottom: 16px;
  }
}

.auth-title {
  font-size: clamp(3.6rem, 4vw, 4.8rem);
  font-weight: 600;
  color: $black-300;
  margin: 0;
}

.features-tile {
  flex: 1;
  padding: 3rem;
  background: $white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba($black, 0.1);

  @media (max-width: 768px) {
    padding: 24px;
    width: 100%;
    max-width: 480px;
  }

  &__title {
    font-size: 2.4rem;
    font-weight: 700;
    color: $black-400;
    margin-bottom: 2rem;

    @media (max-width: 768px) {
      font-size: 2rem;
      margin-bottom: 1.5rem;
    }
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.8rem;
    color: $black-300;
    margin-bottom: 1.5rem;
    line-height: 1.4;

    @media (max-width: 768px) {
      font-size: 1.6rem;
      margin-bottom: 1.2rem;
    }
  }

  &__check {
    color: $brown-200;
    font-weight: bold;
    font-size: 2rem;

    @media (max-width: 768px) {
      font-size: 1.8rem;
    }
  }
}
</style>
